import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useSEO, useJSONLD } from '@/hooks/useSEO';
import { getArticleBySlug, articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  const seoTitle = article ? `${article.title} - 91.cn com` : '文章 - 91.cn com';
  const seoDesc = article ? article.excerpt : '91.cn com文章详情页';
  const seoKeywords = article ? article.tags.join(',') + ',91.cn com' : '91.cn com';
  const canonicalUrl = article ? `https://91cn.com/article/${article.slug}` : 'https://91cn.com';

  useSEO({
    title: seoTitle,
    description: seoDesc,
    keywords: seoKeywords,
    canonicalUrl,
    ogType: 'article',
  });

  useJSONLD(article ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '91.cn com',
    },
    url: canonicalUrl,
    isPartOf: {
      '@type': 'WebSite',
      name: '91.cn com 官方入口',
      url: 'https://91cn.com',
    },
  } : {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '文章未找到',
    url: canonicalUrl,
  });

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-bold text-foreground-950 font-heading mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={[
          { label: '首页', path: '/' },
          { label: article.categoryName, path: `/category/${article.category}` },
          { label: article.title },
        ]} />

        <article className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
            <header className="mb-10">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="text-xs px-3 py-1.5 rounded-md bg-primary-100 text-primary-700 font-medium hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {article.categoryName}
                </Link>
                <span className="text-xs text-foreground-400">{article.readTime}</span>
                <span className="text-xs text-foreground-400">·</span>
                <span className="text-xs text-foreground-400">{article.publishDate}</span>
                <span className="text-xs text-foreground-400">·</span>
                <span className="text-xs text-foreground-400">{article.author}</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading leading-tight mb-4">{article.title}</h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed">{article.excerpt}</p>
            </header>

            <div className="w-full h-64 md:h-96 rounded-lg overflow-hidden mb-10 bg-background-200">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
                title={article.title}
              />
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/60">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700 font-medium whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full py-12 md:py-16 bg-background-100">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-8">相关文章</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group p-5 rounded-lg bg-background-50 hover:bg-background-200/70 transition-all duration-200 cursor-pointer block"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs px-2 py-1 rounded-md bg-accent-100 text-accent-700 font-medium whitespace-nowrap">{ra.categoryName}</span>
                      <span className="text-xs text-foreground-400">{ra.readTime}</span>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors font-heading line-clamp-2">{ra.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">{ra.excerpt}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}